import { apiFetch } from '../../../lib/http';

// G-Score / brain-age API. Live on the backend `brain-age` branch.
//
// Load-bearing invariant: `training/*` endpoints record engagement + adaptive
// difficulty state ONLY. They must never contribute to `assessment/*` scores.
// The two subsystems are on separate paths to make cross-contamination
// impossible to introduce accidentally.
export const cognitionApi = {
  // -- Dashboard ------------------------------------------------------------
  // The single "everything the /g-score landing page needs" call. Returns
  // composite + pillars + streak + today's routine + next-assessment window.
  fetchDashboard({ auth = true } = {}) {
    return apiFetch('/api/v1/cognition/dashboard', { method: 'GET', auth });
  },

  // -- Training (engagement-only, never scored) -----------------------------
  fetchTrainingCatalog({ auth = true } = {}) {
    return apiFetch('/api/v1/cognition/training/games', { method: 'GET', auth });
  },
  fetchGame({ gameId, auth = true }) {
    return apiFetch(`/api/v1/cognition/training/games/${encodeURIComponent(gameId)}`, {
      method: 'GET',
      auth,
    });
  },
  startTrainingSession({ gameId, clientVersion, auth = true }) {
    const query = clientVersion
      ? `?clientVersion=${encodeURIComponent(clientVersion)}`
      : '';
    return apiFetch(
      `/api/v1/cognition/training/games/${encodeURIComponent(gameId)}/start${query}`,
      { method: 'POST', auth }
    );
  },
  // Block-level, not session-level: a session-level submit cannot express
  // in-session difficulty adaptation. Idempotent on (sessionId, clientEventId),
  // so resubmitting an identical body after a network failure is safe.
  submitTrainingBlock({ sessionId, blockIndex, trials, clientVersion, auth = true }) {
    return apiFetch(
      `/api/v1/cognition/training/sessions/${encodeURIComponent(sessionId)}/blocks/${blockIndex}`,
      { method: 'POST', auth, body: { clientVersion, trials } }
    );
  },
  abandonTrainingSession({ sessionId, auth = true }) {
    return apiFetch(
      `/api/v1/cognition/training/sessions/${encodeURIComponent(sessionId)}/abandon`,
      { method: 'POST', auth }
    );
  },

  // -- Assessment (scored, fixed difficulty, fixed cadence) -----------------
  // Distinct URL prefix from training on purpose — makes it structurally
  // impossible for training endpoints to accidentally write to score state.
  fetchAssessmentBattery({ auth = true } = {}) {
    return apiFetch('/api/v1/cognition/assessment/battery', { method: 'GET', auth });
  },
  startAssessment({ pillar, clientVersion, auth = true }) {
    return apiFetch('/api/v1/cognition/assessment/start', {
      method: 'POST',
      auth,
      body: { pillar, clientVersion },
    });
  },
  submitAssessmentBlock({ runId, taskId, blockIndex, trials, auth = true }) {
    return apiFetch(
      `/api/v1/cognition/assessment/runs/${encodeURIComponent(runId)}/tasks/${encodeURIComponent(taskId)}`,
      { method: 'POST', auth, body: { blockIndex, trials } }
    );
  },
  finalizeAssessment({ runId, auth = true }) {
    return apiFetch(`/api/v1/cognition/assessment/runs/${encodeURIComponent(runId)}/finalize`, {
      method: 'POST',
      auth,
    });
  },

  // -- History (score trends across all assessments) -----------------------
  // Returns immutable snapshots of composite + pillar scores per finalized
  // assessment run. Backend authoritative on the timestamps and values —
  // frontend never mutates history entries.
  fetchHistory({ auth = true } = {}) {
    return apiFetch('/api/v1/cognition/history', { method: 'GET', auth });
  },

  fetchPillarHistory({ pillarId, auth = true }) {
    return apiFetch(`/api/v1/cognition/pillars/${encodeURIComponent(pillarId)}/history`, {
      method: 'GET',
      auth,
    });
  },
};
